/*
 * User Detail page.
 *
 * Per-user deep-dive: header (name, dept, location, manager, risk pill
 * with methodology link), KPI cards (sims sent, sims clicked, courses
 * sent, courses completed, completion %), 6-month risk trend chart,
 * activity timeline and an inline breakdown modal trigger.
 *
 * Calculations:
 *   - sims sent / clicked counts from SEND_LOG filtered by email.
 *   - courses sent / completed from COURSE_SEND_LOG.
 *   - completion % = completed / sent.
 *   - risk score and signal breakdown via riskScore.computeBreakdown(email).
 *   - 6-month trend via riskScore.userTrend(email, 6).
 *
 * Postgres mapping:
 *   - one fetch joining `users`, `user_risk_scores` (latest),
 *     `user_risk_trend` (180-day window) and aggregate sim/course counts.
 *   - activity stream from `simulations_sent` + `course_sends`.
 *
 * Integration hook: `useUserDetailQuery(email)` returning
 * { user, score, breakdown, trend, activity, counts }. Cache 1h.
 */
import React from 'react';
import { Box, Grid, Typography, IconButton, Tooltip } from '@mui/material';
import HelpIcon from '@mui/icons-material/HelpOutline';
import { motion } from 'framer-motion';
import MainCard from '../../../components/cards/MainCard';
import CountUp from 'react-countup';
import UserActivityTimeline from './UserActivityTimeline';
import UserRiskTrendChart from './UserRiskTrendChart';
import { USERS, HIGH_RISK_USERS, COURSE_USER_COMPLETIONS, SEND_LOG, COURSE_SEND_LOG, toDateStr } from '../data';
import useDashboardData from '../hooks/useDashboardData';
import { computeBreakdown } from '../riskScore';
import { StatusCell } from './DataTable';
import { BAND_META } from './RiskBreakdownModal';
import UserSusceptibilityCard from './UserSusceptibilityCard';

// Identity metadata under the email (BD-912). Null row means the visit was
// drilled by display name, which the roster cannot match on email.
function UserMetaLine({ row }) {
    if (!row) return null;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.25 }}>
            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                Last login {toDateStr(row.lastLogin) || 'Never'}
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                Created {toDateStr(row.dateCreated) || 'Unknown'}
            </Typography>
            <StatusCell value={row.enabled ? 'Active' : 'Disabled'} />
        </Box>
    );
}

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

function MetricCard({ label, value, unit, sub, color }) {
    return (
        <motion.div variants={fadeUp} style={{ height: '100%', width: '100%', display: 'flex' }}>
            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: 0.5 }}>{label}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography sx={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, color: color || 'text.primary' }}>
                            <CountUp end={parseFloat(String(value).replace(/[^0-9.]/g, '')) || 0} duration={1.4} decimals={String(value).includes('.') ? 1 : 0} />
                        </Typography>
                        {unit && <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary' }}>{unit}</Typography>}
                    </Box>
                    {sub && <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 'auto', pt: 0.5 }}>{sub}</Typography>}
                </Box>
            </MainCard>
        </motion.div>
    );
}

export default function UserDetailPage({ go, drill, userEmail, period }) {
    const riskUser = HIGH_RISK_USERS.find((u) => u.email === userEmail);
    const courseUser = COURSE_USER_COMPLETIONS.find((u) => u.email === userEmail);
    const legacyUser = USERS.find((u) => u.email === userEmail || u.name === userEmail);

    const firstName = riskUser?.firstName || courseUser?.firstName || legacyUser?.name?.split('.')[0]?.trim() || userEmail?.split(' ')[0] || '';
    const lastName = riskUser?.lastName || courseUser?.lastName || '';
    const fullName = legacyUser?.name || (firstName && lastName ? `${firstName} ${lastName}` : userEmail);
    const dept = riskUser?.dept || courseUser?.dept || legacyUser?.dept || '';
    const location = riskUser?.location || courseUser?.location || legacyUser?.location || '';

    const aggUserQ = useDashboardData({ metric: 'simulations', aggregation: 'users', field: userEmail });
    const aggUser = Array.isArray(aggUserQ.data?.riskUsers) ? aggUserQ.data.riskUsers[0] : null;

    // Server score of record: the user's own roster row (risk_snapshot final
    // score + band + avg course score), so this page always agrees with the
    // Users table. Fetched up here because the KPI cards below prefer it too.
    const emailish = String(userEmail || '').includes('@');
    const rosterQ = useDashboardData(
        { metric: 'users', aggregation: 'user_roster', limit: 1, includeDisabled: true, scope: { user: [userEmail] } },
        { skip: !emailish },
    );
    const serverRow = (emailish && Array.isArray(rosterQ.data?.roster))
        ? rosterQ.data.roster.find((r) => r.email === String(userEmail).toLowerCase())
        : null;
    const simEvents = SEND_LOG.filter((e) => e.email === userEmail || `${e.firstName} ${e.lastName}` === userEmail);
    const courseEvents = COURSE_SEND_LOG.filter((e) => e.email === userEmail || `${e.firstName} ${e.lastName}` === userEmail);

    // Prefer server-agg fields when present, but fall back to the client event
    // logs field-by-field — a user drilled from the Course Send Log has course
    // events even when the sim-focused users aggregation carries no course counts.
    const simsSent = aggUser?.sent ?? riskUser?.sent ?? simEvents.length;
    const simsClicked = aggUser?.clicked ?? riskUser?.clicked ?? simEvents.filter((e) => e.status === 'clicked' || e.status === 'clicked_2nd').length ?? legacyUser?.clicks ?? 0;
    const secondStageCount = aggUser?.secondStage ?? simEvents.filter((e) => e.status === 'clicked_2nd').length;
    const reportedCount = aggUser?.reported ?? simEvents.filter((e) => e.status === 'reported').length;
    const coursesSent = aggUser?.coursesSent ?? courseUser?.sent ?? courseEvents.length;
    const coursesCompleted = aggUser?.coursesCompleted ?? courseUser?.completed ?? courseEvents.filter((e) => e.dateCompleted).length;
    const courseCompletionPct = coursesSent > 0 ? Math.round((coursesCompleted / coursesSent) * 100) : (legacyUser?.comp ?? 0);
    // Passed = completed with quiz score at the 80% pass mark, matching the
    // Courses page's Pass Rate definition; avg score prefers the roster row.
    const courseScores = courseEvents
        .map((e) => (e.correctAnswers != null && e.totalQuestions > 0) ? { pct: (e.correctAnswers / e.totalQuestions) * 100, completed: !!e.dateCompleted } : null)
        .filter(Boolean);
    const passedCourses = courseScores.filter((s) => s.completed && s.pct >= 80).length;
    const userAvgScore = serverRow?.avgScore
        ?? (courseScores.length ? Math.round(courseScores.reduce((a, s) => a + s.pct, 0) / courseScores.length) : null);

    const breakdown = computeBreakdown(userEmail);

    // Header badge prefers the server score; the client model stays as the
    // fallback for name-drilled visits the roster can't match (emails only).
    const scoreBadge = serverRow?.riskScore != null
        ? (() => { const m = BAND_META[serverRow.band] || BAND_META.Medium; return { score: serverRow.riskScore, color: m.color, bg: m.bg, text: m.color }; })()
        : (!breakdown.insufficient
            ? { score: breakdown.finalScore, color: breakdown.band.color, bg: breakdown.band.bg, text: breakdown.band.textColor }
            : null);

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '6px 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>

            <motion.div variants={fadeUp}>
                <Box sx={{ display: 'flex', alignItems: 'stretch', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}>{fullName}</Typography>
                            {dept && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{dept}</Typography>}
                            {location && <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{location}</Typography>}
                        </Box>
                        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{userEmail}</Typography>
                        <UserMetaLine row={serverRow} />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {scoreBadge && (
                            <Box
                                sx={{
                                    display: 'inline-flex', alignItems: 'center', gap: 1,
                                    pl: 1.25, pr: 1.5, py: 0.5,
                                    borderRadius: '8px',
                                    bgcolor: scoreBadge.bg,
                                    color: scoreBadge.text,
                                    border: `1px solid ${scoreBadge.color}`,
                                    boxShadow: `0 1px 0 ${scoreBadge.color}22, 0 3px 10px ${scoreBadge.color}1a`,
                                }}
                            >
                                <Box component="span" sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.8 }}>Score</Box>
                                <Typography sx={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1, color: 'inherit' }}>
                                    <CountUp end={scoreBadge.score} duration={1.1} />
                                </Typography>
                            </Box>
                        )}
                        <Tooltip title="How is this calculated?">
                            <IconButton size="small" onClick={() => go && go('risk-methodology', 'Risk methodology')} sx={{ p: '2px' }}>
                                <HelpIcon sx={{ color: 'secondary.main', fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </motion.div>

            {/* Sims row then courses row, four cards each. */}
            <Grid container spacing={1} alignItems="stretch" sx={{ '& > .MuiGrid-item': { display: 'flex' } }}>
                <Grid item xs={6} md={3}><MetricCard label="Sims Sent" value={simsSent} /></Grid>
                <Grid item xs={6} md={3}><MetricCard label="Sims Clicked" value={simsClicked} color={simsClicked > 2 ? '#FF5252' : simsClicked > 0 ? '#FFCA28' : '#66BB6A'} /></Grid>
                <Grid item xs={6} md={3}><MetricCard label="2nd Stage Clicked" value={secondStageCount} color={secondStageCount > 0 ? '#FF5252' : '#66BB6A'} /></Grid>
                <Grid item xs={6} md={3}><MetricCard label="Reported" value={reportedCount} color="#66BB6A" /></Grid>
                <Grid item xs={6} md={3}><MetricCard label="Courses Sent" value={coursesSent} /></Grid>
                <Grid item xs={6} md={3}><MetricCard label="Course Completion" value={courseCompletionPct} unit="%" color={courseCompletionPct >= 80 ? '#66BB6A' : courseCompletionPct >= 50 ? '#FFCA28' : '#FF5252'} /></Grid>
                <Grid item xs={6} md={3}><MetricCard label="Passed Courses" value={passedCourses} color="#66BB6A" /></Grid>
                <Grid item xs={6} md={3}><MetricCard label="Avg Score" value={userAvgScore ?? 0} unit="%" color={(userAvgScore ?? 0) >= 80 ? '#66BB6A' : (userAvgScore ?? 0) >= 50 ? '#FFCA28' : '#FF5252'} /></Grid>
            </Grid>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch', gap: 1 }}>
                {/* Trend renders for anyone the SERVER has scored (its history feed
                    self-hides below 2 points); client insufficiency is only the
                    fallback gate for name-drilled visits. */}
                {(serverRow?.riskScore != null || !breakdown.insufficient) && (
                    <Box sx={{ flex: { xs: '1 1 auto', md: '1 1 50%' }, minWidth: 0 }}>
                        <UserRiskTrendChart email={userEmail} />
                    </Box>
                )}
                <Box sx={{ flex: { xs: '1 1 auto', md: '1 1 50%' }, minWidth: 0 }}>
                    <UserSusceptibilityCard email={userEmail} />
                </Box>
            </Box>

            <motion.div variants={fadeUp}>
                <UserActivityTimeline simEvents={simEvents} courseEvents={courseEvents} drill={drill} />
            </motion.div>

        </motion.div>
    );
}
